import { Button } from "@/components/button/Button";
import { Card } from "@/components/card/Card";
import { APPROVAL } from "@/shared";
import type { ToolUIPart } from "ai";
import type { ToolMeta } from "@/lib/toolFactory";
import {
  clientToolExecutions,
  isClientSideTool,
} from "@/lib/clientToolExecutions";
import { useState } from "react";
import { AjaxParamConfirmation } from "./AjaxParamConfrimation";
import { Loader } from "@/components/loader/Loader";

interface ToolResultWithContent {
  content: Array<{ type: string; text: string }>;
}

function isToolResultWithContent(
  result: unknown
): result is ToolResultWithContent {
  return (
    typeof result === "object" &&
    result !== null &&
    "content" in result &&
    Array.isArray((result as ToolResultWithContent).content)
  );
}

interface ToolInvocationCardProps {
  toolUIPart: ToolUIPart;
  toolCallId: string;
  needsConfirmation: boolean;
  onSubmit: ({
    toolCallId,
    result,
  }: {
    toolCallId: string;
    result: string;
  }) => void;
  toolMeta: ToolMeta;
  userEmail?: string;
}

// Tool definitions can be made with {paramKey}(s) in their approvalMessage
// This will replace the parameter keys with the actual values from the tool invocation args
// Or custom messages can be used to provide more context - there are no examples for this yet
function templateString(str: string | undefined, params: Record<string, any>) {
  if (!str) return "";
  return str.replace(/{(\w+)}/g, (_, key) => params[key] ?? `{${key}}`);
}

export function ToolInvocationCard({
  toolUIPart,
  toolCallId,
  needsConfirmation,
  onSubmit,
  toolMeta,
  userEmail,
}: ToolInvocationCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isParamLoading, setIsParamLoading] = useState(false);
  const toolName = toolUIPart.type.replace("tool-", "");

  const handleApprove = async () => {
    setIsLoading(true);

    try {
      let result: string;

      // Check if this tool requires client-side execution
      if (toolMeta.clientSideExecution && isClientSideTool(toolName)) {
        // Execute client-side function
        const clientExecution = clientToolExecutions[toolName];
        result = await clientExecution(toolUIPart.input as any);
      } else {
        // Normal server-side approval
        result = APPROVAL.YES;
      }

      onSubmit({ toolCallId, result });
    } catch (error) {
      // On error, send the error message as the result
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      onSubmit({
        toolCallId,
        result: `Error: ${errorMessage}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      className={`p-4 mb-3 w-full max-w-[500px] rounded-md assistant-bg ${
        needsConfirmation ? "" : "border-[#F48120]/30"
      } overflow-hidden`}
    >
      <div className={`transition-all duration-200 max-h-[200px] opacity-100`}>
        <div className="overflow-y-auto" style={{ maxHeight: "180px" }}>
          <div>
            <h5 className="mb-1 text-muted-foreground">
              {toolMeta.displayName}
            </h5>
            {toolMeta.useAjaxParamConfirmation ? (
              toolUIPart.state === "input-available" ||
              toolUIPart.state === "output-available" ? (
                <AjaxParamConfirmation
                  toolName={toolName}
                  params={toolUIPart.input as Record<string, any>}
                  apiEndpoint={toolMeta.ajaxParamEndpoint}
                  userEmail={userEmail}
                  onLoadingChange={setIsParamLoading}
                />
              ) : (
                <div className="bg-background/80 p-2 pl-0 rounded-md text-sm flex items-center gap-2">
                  <Loader size={16} />
                  <span className="text-muted-foreground">
                    Gathering details...
                  </span>
                </div>
              )
            ) : (
              <div className="bg-background/80 p-2 pl-0 rounded-md text-sm overflow-auto whitespace-pre-wrap break-words max-w-[450px]">
                {toolUIPart.input
                  ? templateString(
                      toolMeta.approvalMessage,
                      toolUIPart.input as Record<string, any>
                    )
                  : null}
              </div>
            )}
          </div>

          {/* {needsConfirmation && toolInvocation.state === "call" && ( */}
          <div className="flex gap-2 justify-start">
            <Button
              variant="secondary"
              size="sm"
              className="reject-btn"
              disabled={
                toolUIPart.state === "output-available" ||
                isLoading ||
                isParamLoading
              }
              onClick={() => onSubmit({ toolCallId, result: APPROVAL.NO })}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="approve-btn"
              disabled={
                toolUIPart.state === "output-available" ||
                isLoading ||
                isParamLoading
              }
              onClick={handleApprove}
            >
              {isLoading
                ? "Processing..."
                : isParamLoading
                  ? "Loading..."
                  : "Approve"}
            </Button>
          </div>
          {/* )} */}

          {!needsConfirmation &&
            toolUIPart.state === "output-available" &&
            toolMeta.showResult && (
              <div className="mt-3 border-t border-[#F48120]/10 pt-3">
                <h5 className="mb-1 text-muted-foreground">Result:</h5>
                <pre className="bg-background/80 p-2 rounded-md text-sm overflow-auto whitespace-pre-wrap break-words max-w-[450px]">
                  {(() => {
                    const result = toolUIPart.output;
                    if (isToolResultWithContent(result)) {
                      return result.content
                        .map((item: { type: string; text: string }) => {
                          if (
                            item.type === "text" &&
                            item.text.startsWith("\n~ Page URL:")
                          ) {
                            const lines = item.text.split("\n").filter(Boolean);
                            return lines
                              .map(
                                (line: string) =>
                                  `- ${line.replace("\n~ ", "")}`
                              )
                              .join("\n");
                          }
                          return item.text;
                        })
                        .join("\n");
                    }
                    return JSON.stringify(result, null, 2);
                  })()}
                </pre>
              </div>
            )}
        </div>
      </div>
    </Card>
  );
}
